import { supabase } from "../supabase";
import {
  getStoreOrderOrigin,
  isStoreOrderDeliveredStatus,
} from "./storeLogistics";

export const SALE_STORE_ORDER_SELECT =
  "id, sale_id, status, delivery_type, created_at, shipping_address, customer_snapshot";

const normalizeSaleBillingSource = (value) => String(value || "").trim().toLowerCase();
const WEB_BILLING_SOURCE_TOKENS = ["tienda", "online", "web"];

export const getSaleStoreOrders = (sale) =>
  (Array.isArray(sale?.store_orders) ? sale.store_orders : []).filter(Boolean);

export const getSaleStoreOrdersByOrigin = (sale, origin) =>
  getSaleStoreOrders(sale).filter(
    (order) => getStoreOrderOrigin(order) === String(origin || "").trim().toLowerCase(),
  );

export const isSaleFromOnlineStore = (sale) => {
  const saleStoreOrders = getSaleStoreOrders(sale);

  if (saleStoreOrders.some((order) => getStoreOrderOrigin(order) === "web")) {
    return true;
  }

  if (saleStoreOrders.some((order) => getStoreOrderOrigin(order) === "pos")) {
    return false;
  }

  const billingSource = normalizeSaleBillingSource(sale?.billing_source);
  return WEB_BILLING_SOURCE_TOKENS.some((token) => billingSource.includes(token));
};

export const getDeliveredSaleStoreOrder = (sale, options = {}) => {
  const { origin = "" } = options;
  const targetOrders = origin
    ? getSaleStoreOrdersByOrigin(sale, origin)
    : getSaleStoreOrders(sale);

  return targetOrders
    .filter((order) => isStoreOrderDeliveredStatus(order?.status))
    .sort(
      (left, right) =>
        new Date(right?.created_at || 0).getTime() -
        new Date(left?.created_at || 0).getTime(),
    )[0] || null;
};

export const isSaleDeliveredThroughStoreOrder = (sale, options = {}) =>
  Boolean(getDeliveredSaleStoreOrder(sale, options));

export const isSaleAwaitingWebFulfillment = (sale) => {
  if (!isSaleFromOnlineStore(sale)) {
    return false;
  }

  const webOrders = getSaleStoreOrdersByOrigin(sale, "web");
  if (webOrders.length === 0) {
    return normalizeSaleBillingSource(sale?.status) === "pending";
  }

  return !webOrders.some((order) => isStoreOrderDeliveredStatus(order?.status));
};

export async function attachStoreOrdersToSales({
  sales,
  organizationId,
  supabaseClient = supabase,
}) {
  const safeSales = Array.isArray(sales) ? sales.filter(Boolean) : [];
  const saleIds = Array.from(new Set(safeSales.map((sale) => sale?.id).filter(Boolean)));

  if (!organizationId || saleIds.length === 0) {
    return {
      data: safeSales.map((sale) => ({
        ...sale,
        store_orders: getSaleStoreOrders(sale),
      })),
      error: null,
    };
  }

  // Fast path: si la query original ya embebió store_orders (vía Postgrest fk
  // embed: `select: '*, store_orders(...)'`), no hace falta refetch.
  // Esto evita el N+1 que Sentry detectaba en VentasHistorial (ref IMPULZA-3).
  const allHaveStoreOrders = safeSales.every((sale) => Array.isArray(sale?.store_orders));
  if (allHaveStoreOrders) {
    return {
      data: safeSales.map((sale) => ({
        ...sale,
        store_orders: getSaleStoreOrders(sale),
      })),
      error: null,
    };
  }

  const CHUNK_SIZE = 200;
  const chunks = [];
  for (let i = 0; i < saleIds.length; i += CHUNK_SIZE) {
    chunks.push(saleIds.slice(i, i + CHUNK_SIZE));
  }

  const responses = await Promise.all(
    chunks.map((chunk) =>
      supabaseClient
        .from("store_orders")
        .select(SALE_STORE_ORDER_SELECT)
        .eq("organization_id", organizationId)
        .in("sale_id", chunk),
    ),
  );

  const firstError = responses.find((r) => r.error)?.error || null;
  if (firstError) {
    return { data: safeSales, error: firstError };
  }

  const allOrders = responses.flatMap((r) => r.data || []);

  const bySaleId = new Map();
  allOrders.forEach((order) => {
    if (!order?.sale_id) return;
    const rows = bySaleId.get(order.sale_id) || [];
    rows.push(order);
    bySaleId.set(order.sale_id, rows);
  });

  return {
    data: safeSales.map((sale) => ({
      ...sale,
      store_orders: bySaleId.get(sale.id) || getSaleStoreOrders(sale),
    })),
    error: null,
  };
}
